import {
  FC,
  ReactElement,
  useMemo,
} from "react";

import { ArrowSmallDownIcon } from "@repo/edtech-assets";
import { EdtechContentClient } from "@repo/edtech-content-client";
import { useIsMobile } from "@repo/edtech-shared";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Loader,
} from "@repo/edtech-ui-kit";
import clsx from "clsx";

import {
  MOBILE_WIDTH,
  TestQuestionData,
  TestQuestionFrontType,
  useTestSolutionActionsContext,
  useTestSolutionValuesContext,
} from "../../../../../../model";
import { t } from "../../../../../../origins";
import { Timer } from "../timer/Timer";

import st from "./TestSolutionLayout.module.scss";

const QUESTION_DESCRIPTION_BY_TYPE = {
  [ TestQuestionFrontType.Open ]:
    "Напишите ответ на вопрос в окне ниже",
  [ TestQuestionFrontType.ClosedMany ]: "Выберите несколько ответов",
  [ TestQuestionFrontType.ClosedOne ]: "Выберите один вариант ответа",
  [ TestQuestionFrontType.Sequence ]:
    "Нажмите на элементы в правильном порядке",
  [ TestQuestionFrontType.Survey ]: "",
  [ TestQuestionFrontType.Matching ]:
    "Соотнесите элементы из левой колонки с подходящими из правой, перетаскивая их мышью",
};

type Props = {
  questionData: TestQuestionData,
  questionsCount: number,
  isQuestionsLoading?: boolean,
  children: ReactElement,
  isSurvey: boolean,
};

export const TestSolutionLayout: FC<Props> = ({
  questionData,
  questionsCount,
  isQuestionsLoading,
  children,
  isSurvey,
}) => {
  const {
    questionType,
    currentQuestionIndex,
    isNextQuestionDisabled,
    timer,
    isCommentRequiredAndEmpty,
  } = useTestSolutionValuesContext();

  const {
    onClickPrevQuestion,
    onClickNextQuestion,
  } = useTestSolutionActionsContext();

  const {
    question: title,
    surveyComment2,
    stars,
    surveyComment1,
    descriptionLongread,
  } = questionData;

  const isMobile = useIsMobile(MOBILE_WIDTH);

  const description = questionType ?
    QUESTION_DESCRIPTION_BY_TYPE[ questionType ] :
    "";

  const isLastQuestion = questionsCount === currentQuestionIndex + 1;

  const finishText = useMemo(() => {
    if (isMobile) return t("testSolutionLayout.finishMobile");
    if (isSurvey) return t("testSolutionLayout.finishSurvey");
    return t("testSolutionLayout.finishTest");
  }, [ isMobile, isSurvey ]);

  return (
    <div className={clsx(st.wrap, isSurvey && st.survey)}>
      <div className={st.top_line}>
        <div className={st.question_counter}>
          Вопрос {currentQuestionIndex + 1} / {questionsCount}
        </div>
        {
          timer !== null && (
            <Timer time={timer} />
          )
        }
      </div>
      {
        isQuestionsLoading ?
          (
            <div className={st.loader_wrap}>
              <Loader />
            </div>
          ) :
          (
            <>
              <h3 className={st.title}>{title}</h3>
              {
                isSurvey && surveyComment1 && surveyComment2 && (
                  <div className={st.survey_comments}>
                    <p className={st.survey_comment}>{`1 — ${surveyComment1}`}</p>
                    <p className={st.survey_comment}>{`${stars} — ${surveyComment2}`}</p>
                  </div>
                )
              }
              {
                descriptionLongread && (
                  <EdtechContentClient
                    content={descriptionLongread}
                  />
                )
              }
              <p className={st.description}>{description}</p>
              <div className={clsx(st.children, isSurvey && st.survey)}>
                {children}
              </div>
              <div className={st.actions}>
                {
                  currentQuestionIndex > 0 ?
                    (
                      <Button
                        size={ButtonSize.Medium}
                        variant={ButtonVariant.Outline}
                        onClick={onClickPrevQuestion}
                      >
                        <ArrowSmallDownIcon className={st.left_arrow_icon} />
                        Назад
                      </Button>
                    ) :
                    (
                      <div />
                    )
                }
                <Button
                  disabled={isNextQuestionDisabled || isCommentRequiredAndEmpty}
                  size={ButtonSize.Medium}
                  variant={ButtonVariant.Primary}
                  onClick={() => onClickNextQuestion(questionsCount)}
                >
                  <span>{
                    isLastQuestion ?
                      finishText :
                      "Дальше"
                  }
                  </span>
                  <ArrowSmallDownIcon className={st.right_arrow_icon} />
                </Button>
              </div>
            </>
          )
      }
    </div>
  );
};
